import * as React from "react";
import { Namespace, TranslationKey, useT } from "@racoon/i18n";
import { ConnectProto } from "@racoon/api";
import { Popover, PopoverContent, PopoverTrigger, Separator, cn } from "@racoon/ui";
import { fromTimestamp } from "@racoon/lib";
import { isTtnApproved } from "~/shared/lib/shift-status";
import { TruckImage } from "~/shared/ui/truck-image";
import { countsInTotal } from "../model/month-total";

/**
 * Staged (not yet saved) plan edit for a cell. A click paints a future day with
 * the plan type picked in the footer, so the staged value has to say WHICH plan
 * is pending, not merely that one is.
 */
export type StagedEdit =
  | { kind: "plan"; planType: ConnectProto.ShiftPlanType }
  | { kind: "delete" };

const CellState = ConnectProto.ShiftCellState;
const PlanType = ConnectProto.ShiftPlanType;
const ShiftStatus = ConnectProto.DriverOnShiftStatus;

/** Inner 32px block fill per cell state — colors from the Figma Day Cell variants. */
const STATE_FILL: Record<number, string> = {
  [CellState.ENDED]: "bg-[#364252] opacity-75",
  [CellState.ACTIVE]: "bg-[#22c55e]",
  [CellState.NOT_STARTED]: "bg-[#2563eb]",
  [CellState.PLANNED]: "bg-[#93c5fd]",
  [CellState.MISSED]: "bg-[#dc2626]",
};

/**
 * A planned cell is coloured by its plan type, not by its state: all three are
 * PLANNED. Tokens from the Figma tab group (21081:35617), whose swatches are
 * the same colours the matrix paints: status/assigned/selected, surface/ai,
 * surface/warning.
 */
const PLAN_FILL: Record<number, string> = {
  [PlanType.DAY]: "bg-[#93c5fd]",
  [PlanType.NIGHT]: "bg-[#8e51ff]",
  [PlanType.DAY_OFF]: "bg-[#f97316]",
};

/** Fill for a plan, falling back to the day-shift colour for an unset type. */
export const planFill = (planType?: number) =>
  PLAN_FILL[planType ?? PlanType.DAY] ?? PLAN_FILL[PlanType.DAY];

const PLAN_LABEL_KEY: Record<number, string> = {
  [PlanType.DAY]: "legend.day",
  [PlanType.NIGHT]: "legend.night",
  [PlanType.DAY_OFF]: "legend.dayOff",
};

/** Translation key describing a cell state — used for the cell's screen-reader name. */
const STATE_LABEL_KEY: Record<number, string> = {
  [CellState.ENDED]: "badge.ended",
  [CellState.ACTIVE]: "badge.active",
  [CellState.NOT_STARTED]: "badge.notStarted",
  [CellState.PLANNED]: "badge.planned",
  [CellState.MISSED]: "badge.missed",
};

/**
 * How a stored cell is drawn and named. A PLANNED cell goes by its plan type —
 * day, night and day off are all PLANNED, so the state alone cannot tell them apart.
 */
const cellLook = (cell: ConnectProto.ShiftScheduleCell) =>
  cell.state === CellState.PLANNED
    ? {
        fill: planFill(cell.planType),
        key: PLAN_LABEL_KEY[cell.planType ?? PlanType.DAY] ?? STATE_LABEL_KEY[CellState.PLANNED],
      }
    : { fill: STATE_FILL[cell.state], key: STATE_LABEL_KEY[cell.state] };

// Shift status pill — Figma Shift status variants: assigned #dbeafe/#2563eb,
// completed #22c55e, cancelled #667489. TTN approved keeps the ended grey until
// the design settles its colour.
const BADGE = {
  notStarted: { key: "badge.notStarted", className: "bg-[#dbeafe] text-[#2563eb]" },
  active: { key: "badge.active", className: "bg-[#22c55e] text-white" },
  ended: { key: "badge.ended", className: "bg-[#667489] text-white" },
  ttnApproved: { key: "badge.ttnApproved", className: "bg-[#667489] text-white" },
  missed: { key: "badge.missed", className: "bg-[#dc2626] text-white" },
};

/**
 * Badge for one shift. Two shifts of the same day can differ, so it follows the
 * shift's own status; "no-show" is a cell-level verdict (a past day whose shift
 * never started), so it falls back to the cell state.
 */
const badgeForShift = (shift: ConnectProto.DriverOnShift, cellState: number) => {
  if (shift.status === ShiftStatus.ENDED)
    return isTtnApproved(shift) ? BADGE.ttnApproved : BADGE.ended;
  if (shift.status === ShiftStatus.ACTIVE) return BADGE.active;
  return cellState === CellState.MISSED ? BADGE.missed : BADGE.notStarted;
};

/**
 * Time and date sit in separate columns in the design, hence the tuple. The date
 * is assembled from parts to read like the design ("21 Авг 2026") instead of the
 * locale default ("21 авг. 2026 г.").
 */
const formatDateTime = (date: Date, locale: string) => {
  const parts = new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? "";
  const month = part("month").replace(/\.$/, "");

  return {
    time: date.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" }),
    date: `${part("day")} ${month.charAt(0).toUpperCase()}${month.slice(1)} ${part("year")}`,
  };
};

/** "21 August" for a YYYY-MM-DD day, built from parts to stay timezone-proof. */
const formatDay = (date: string, locale: string) => {
  const [year, month, day] = date.split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString(locale, {
    day: "numeric",
    month: "long",
  });
};

/** One label/value line of the card's detail list — label column is 160px wide. */
const DetailRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex items-start justify-between gap-4">
    <span className="w-40 shrink-0 text-sm font-medium leading-4 tracking-[-0.2px] text-[#667489]">
      {label}
    </span>
    <span className="flex items-center gap-2 text-sm font-medium leading-4 tracking-[-0.2px] text-[#242a35]">
      {children}
    </span>
  </div>
);

/** Started/ended timestamp — "06:43   21 Aug 2026", two columns as in the design. */
const DateTimeValue = ({ date, locale }: { date: Date; locale: string }) => {
  const { time, date: day } = formatDateTime(date, locale);
  return (
    <>
      <span>{time}</span>
      <span>{day}</span>
    </>
  );
};

type ShiftCardProps = {
  shift: ConnectProto.DriverOnShift;
  cellState: number;
  driverName: string;
  locale: string;
};

/**
 * Figma "Shift Hover Card" (20161:34748 ended / 34749 active / 34750 planned):
 * a 400px card — truck header, one separator, then the status pill with the
 * shift's times. Every shift of the day gets its own card.
 */
const ShiftCard = ({ shift, cellState, driverName, locale }: ShiftCardProps) => {
  const t = useT();
  const truck = shift.truck;
  const truckType = truck?.truckType;
  const badge = badgeForShift(shift, cellState);

  const model = truckType?.model || truck?.model;
  // The design labels the truck by its type ("Мусоровоз ТБО", "Мультилифт",
  // "Бункеровоз") — the enum labels; the org's own type name is the fallback.
  const typeEnum = ConnectProto.TruckType[truckType?.type || truck?.type || 0];
  const typeLabel =
    typeEnum && typeEnum !== "UNDEFINED"
      ? t(Namespace.Enum, `TRUCK_TYPE_${typeEnum}` as TranslationKey<Namespace.Enum>)
      : truckType?.name;

  // A shift that has not gone to work yet carries no facts, so the design's
  // planned card (20161:34750) is only the truck header, the status pill and
  // the shift number — no times, no routes, no incidents.
  // Which statuses count as "not started" mirrors the backend's
  // cellStateForShift: it paints ACTIVE and ENDED and treats everything else as
  // a shift the driver has not gone to work on yet. Listing the pre-start
  // statuses instead would miss CONFIRMATION and anything added later, and the
  // popover would then disagree with the colour of its own cell.
  const isPlanned = shift.status !== ShiftStatus.ACTIVE && shift.status !== ShiftStatus.ENDED;

  // "Начато" is when the driver actually went to work, which is started_at.
  // tw_start_at is only the planned window; it is the fallback for shifts that
  // predate the field, since using it as the primary would report a shift begun
  // at 08:00 as begun at 06:00 and inflate the duration by the difference.
  const startedAt = fromTimestamp(shift.startedAt) ?? fromTimestamp(shift.twStartAt);
  const endedAt = shift.status === ShiftStatus.ENDED ? fromTimestamp(shift.completedAt) : null;
  // A running shift is measured against now, a finished one against its end.
  const durationEnd = shift.status === ShiftStatus.ACTIVE ? new Date() : endedAt;
  const durationMin =
    startedAt && durationEnd && durationEnd > startedAt
      ? Math.floor((durationEnd.getTime() - startedAt.getTime()) / 60_000)
      : null;

  return (
    <div className="w-full shrink-0 overflow-hidden rounded-xl border border-border-primary bg-white shadow-[0px_4px_20px_-10px_rgba(0,0,0,0.07)]">
      <div className="flex items-center gap-2.5 p-4">
        <div className="flex min-w-0 flex-1 flex-col gap-4">
          <div className="flex items-center gap-4">
            {truck?.plateNumber && (
              <span className="shrink-0 rounded-[4px] border border-[#364252] p-1 text-sm font-medium leading-4 tracking-[-0.2px] text-[#242a35]">
                {truck.plateNumber}
              </span>
            )}
            <span className="truncate text-sm font-medium leading-4 tracking-[-0.2px] text-[#242a35]">
              {driverName}
            </span>
          </div>
          <div className="flex items-start gap-4 text-xs font-medium leading-4 tracking-[-0.01em] text-[#667489]">
            {(truckType?.manufacturer || model) && (
              <div className="flex shrink-0 items-center gap-1">
                {truckType?.manufacturer && <span>{truckType.manufacturer}</span>}
                {model && <span>{model}</span>}
              </div>
            )}
            {typeLabel && <span className="truncate">{typeLabel}</span>}
          </div>
        </div>
        <div className="flex h-[60px] w-20 shrink-0 items-center justify-center">
          <TruckImage truck={truck} />
        </div>
      </div>

      {/* the design's single separator, between truck header and shift details */}
      <div className="h-px w-full bg-border-primary" />

      <div className="flex flex-col gap-4 p-4">
        <div className="flex items-center justify-between gap-2">
          <span
            className={cn(
              "rounded-2xl px-3 py-1 text-xs font-medium leading-4 tracking-[-0.01em]",
              badge.className,
            )}
          >
            {t(Namespace.ShiftSchedule, badge.key as never)}
          </span>
          <span className="text-sm font-normal leading-4 tracking-[-0.01em] text-[#667489]">
            S{String(shift.id)}
          </span>
        </div>

        {!isPlanned && (
          <div className="flex flex-col gap-2">
            {startedAt && (
              <DetailRow label={t(Namespace.ShiftSchedule, "popover.startedAt")}>
                <DateTimeValue date={startedAt} locale={locale} />
              </DetailRow>
            )}
            {endedAt && (
              <DetailRow label={t(Namespace.ShiftSchedule, "popover.endedAt")}>
                <DateTimeValue date={endedAt} locale={locale} />
              </DetailRow>
            )}
            {durationMin !== null && (
              <DetailRow label={t(Namespace.ShiftSchedule, "popover.duration")}>
                {Math.floor(durationMin / 60)}
                {t(Namespace.ShiftSchedule, "hoursShort")} {durationMin % 60}
                {t(Namespace.ShiftSchedule, "minutesShort")}
              </DetailRow>
            )}
            <DetailRow label={t(Namespace.ShiftSchedule, "popover.completedRoutes")}>
              {shift.completedRouteCount}
            </DetailRow>
            <DetailRow label={t(Namespace.ShiftSchedule, "popover.incidents")}>
              {/* green for none, red once something was reported — the value
                  itself is what the dispatcher scans for */}
              <span className={shift.hasIncidents ? "text-[#dc2626]" : "text-[#16a34a]"}>
                {t(
                  Namespace.ShiftSchedule,
                  shift.hasIncidents ? "popover.incidents.some" : "popover.incidents.none",
                )}
              </span>
            </DetailRow>
          </div>
        )}
      </div>
    </div>
  );
};

type ShiftPopoverProps = {
  cell: ConnectProto.ShiftScheduleCell;
  driverName: string;
  locale: string;
  children: React.ReactNode;
};

/**
 * Popover over a day cell: one card per shift, stacked. The popover itself is
 * stripped of its panel styling so each card reads as its own surface.
 */
const ShiftPopover = ({ cell, driverName, locale, children }: ShiftPopoverProps) => (
  <Popover>
    <PopoverTrigger asChild>{children}</PopoverTrigger>
    <PopoverContent
      align="start"
      sideOffset={4}
      className="!w-[400px] !border-0 !bg-transparent !p-0 !shadow-none"
    >
      <div className="flex max-h-[min(28rem,var(--radix-popover-content-available-height))] flex-col gap-2 overflow-y-auto overflow-x-hidden overscroll-contain">
        {cell.driverOnShifts.map((shift) => (
          <ShiftCard
            key={String(shift.id)}
            shift={shift}
            cellState={cell.state}
            driverName={driverName}
            locale={locale}
          />
        ))}
      </div>
    </PopoverContent>
  </Popover>
);

type ScheduleCellProps = {
  cell?: ConnectProto.ShiftScheduleCell;
  /** ISO day (YYYY-MM-DD) this cell stands for — cells without a shift have no other source. */
  date: string;
  driverName: string;
  editable: boolean;
  staged?: StagedEdit;
  locale: string;
  onToggle: () => void;
};

/**
 * One 40×40 day cell. Shift-backed cells open the info popover; empty and
 * PLANNED future cells toggle a staged plan edit (flushed by the Save button).
 */
export const ScheduleCell = ({
  cell,
  date,
  driverName,
  editable,
  staged,
  locale,
  onToggle,
}: ScheduleCellProps) => {
  const t = useT();

  // Any cell carrying a shift opens the info popover — including a MISSED cell
  // derived from a never-started shift. Plan-only cells have none.
  const shiftCount = cell?.driverOnShifts.length ?? 0;
  const isShiftBacked = shiftCount > 0;

  // Staged edits override what the server sent: a staged delete renders the
  // cell empty, a staged plan renders in that plan's colour.
  // A PLANNED cell is coloured by its plan type — day, night and day off are
  // all PLANNED, so the state alone cannot tell them apart.
  const fill =
    staged?.kind === "delete"
      ? undefined
      : staged?.kind === "plan"
        ? planFill(staged.planType)
        : cell && cellLook(cell).fill;

  // The cell is a coloured square with no text, so its whole meaning has to go
  // into the accessible name: whose day it is, which day, and its state.
  const stateKey =
    staged?.kind === "delete"
      ? undefined
      : staged?.kind === "plan"
        ? (PLAN_LABEL_KEY[staged.planType] ?? STATE_LABEL_KEY[CellState.PLANNED])
        : cell && cellLook(cell).key;
  const label = [
    driverName,
    formatDay(date, locale),
    stateKey && t(Namespace.ShiftSchedule, stateKey as never),
  ]
    .filter(Boolean)
    .join(", ");

  const box = (
    <button
      type="button"
      aria-label={label}
      onClick={isShiftBacked ? undefined : editable ? onToggle : undefined}
      className={cn(
        // transparent so the row-hover tint behind the grid shows through
        "size-10 shrink-0 border border-[#e2e8f0] p-1",
        (isShiftBacked || editable) && "cursor-pointer",
        !isShiftBacked && !editable && "cursor-default",
      )}
    >
      {fill && (
        <div
          className={cn(
            "flex size-full items-center justify-center rounded-[4px]",
            fill,
            staged && "ring-2 ring-[#2563eb]/60",
          )}
        >
          {/* more than one shift that day — the count is the only hint the
              single coloured block can carry */}
          {shiftCount > 1 && (
            <span className="text-[10px] font-semibold leading-[10px] text-white">
              {shiftCount}
            </span>
          )}
        </div>
      )}
      {!fill && staged?.kind === "delete" && (
        <div className="size-full rounded-[4px] ring-2 ring-[#2563eb]/60" />
      )}
    </button>
  );

  if (isShiftBacked && cell) {
    return (
      <ShiftPopover cell={cell} driverName={driverName} locale={locale}>
        {box}
      </ShiftPopover>
    );
  }
  return box;
};

type MonthTotalProps = {
  row: ConnectProto.DriverShiftScheduleRow;
  /** The row's total with unsaved edits folded in, as the badge shows it. */
  total: number;
};

/**
 * The month total, opening what it counts: the row's days by how they are drawn
 * in the grid, adding up to the saved total; no-shows are listed apart, as not
 * counted. Cancelled shifts are left out of the schedule by the backend.
 */
export const MonthTotal = ({ row, total }: MonthTotalProps) => {
  const t = useT();
  const counts = new Map<string, { fill?: string; count: number }>();
  let missed = 0;
  for (const cell of row.cells) {
    if (cell.state === CellState.MISSED) missed++;
    if (!countsInTotal(cell)) continue;
    const { fill, key } = cellLook(cell);
    counts.set(key, { fill, count: (counts.get(key)?.count ?? 0) + 1 });
  }

  const countRow = (
    fill: string | undefined,
    labelKey: string,
    count: number,
    testId = "month-total-row",
  ) => (
    <div
      key={labelKey}
      data-testid={testId}
      className="flex items-center gap-2 text-xs leading-4 text-text-body"
    >
      <span className={cn("size-4 shrink-0 rounded", fill)} />
      <span className="min-w-0 flex-1 truncate">
        {t(Namespace.ShiftSchedule, labelKey as never)}
      </span>
      <span className="font-medium">{count}</span>
    </div>
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="month-total"
          aria-label={`${row.driverName}: ${t(Namespace.ShiftSchedule, "total.breakdown")}`}
          className="flex size-9 cursor-pointer items-center justify-center rounded-lg border border-border-primary bg-slate-100 px-2 text-xs font-medium leading-4 tracking-[-0.01px] text-text-body hover:border-slate-300"
        >
          {total}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={4} className="!w-64 !p-4">
        <p className="mb-3 truncate text-sm font-medium leading-4 tracking-[-0.2px] text-text-body">
          {row.driverName}
        </p>
        {counts.size === 0 && missed === 0 ? (
          <p data-testid="month-total-empty" className="text-xs leading-4 text-text-subtle">
            {t(Namespace.ShiftSchedule, "total.empty")}
          </p>
        ) : (
          <>
            <div className="flex flex-col gap-2">
              {Array.from(counts, ([key, { fill, count }]) => countRow(fill, key, count))}
            </div>
            {missed > 0 && (
              <>
                <Separator className="my-3" />
                {countRow(
                  STATE_FILL[CellState.MISSED],
                  STATE_LABEL_KEY[CellState.MISSED],
                  missed,
                  "month-total-missed",
                )}
                <p className="mt-1 text-[11px] leading-4 text-text-subtle">
                  {t(Namespace.ShiftSchedule, "total.notCounted")}
                </p>
              </>
            )}
          </>
        )}
      </PopoverContent>
    </Popover>
  );
};
